import { FC, ReactNode, memo } from "react";

/**
 * Main component interface
 *
 * The Main component serves as the primary content wrapper for pages,
 * providing semantic HTML structure and animation support.
 */
export interface IMain {
  /** Additional CSS classes applied to the main element */
  className?: string;
  /** Page content to be rendered inside the main element */
  children: ReactNode
}

/**
 * Main component
 *
 * A semantic wrapper component that serves as the primary content container
 * for pages. The Main component provides proper HTML structure and includes
 * animation support for enhanced user experience.
 *
 * @example
 * ```tsx
 * <Main>
 *   <Section size="xl">
 *     <Container>
 *       <h1>Page Title</h1>
 *       <p>Page content...</p>
 *     </Container>
 *   </Section>
 * </Main>
 * ```
 */
const Main: FC<IMain> = ({ className, children }) => (
  <main data-component="Main" id="main" className={`animate--fade-in ${className}`}>
    { children }
  </main>
);

export default memo(Main);
